type ApiErrorDetail = {
  row?: number | null;
  field?: string | null;
  message?: string | null;
};

type ModalLike = {
  show: (payload: {
    type: "error";
    title: string;
    message: string;
    autoClose: number | null;
  }) => unknown;
} | null | undefined;

type ExtractApiErrorOptions = {
  fallbackMessage?: string;
  fieldLabels?: Record<string, string>;
  maxDetails?: number;
};

type ShowApiErrorOptions = ExtractApiErrorOptions & {
  title: string;
  autoClose?: number | null;
};

const defaultFieldLabels: Record<string, string> = {
  email: "email",
  phone: "telefone",
  productName: "produto",
  startDate: "data de inicio",
  endDate: "data de termino",
  affiliateId: "afiliado",
  companyDocument: "cnpj",
  postalBoxNumber: "caixa postal",
  legalRepresentativeName: "representante legal",
  legalRepresentativeDocument: "cpf do representante",
  birthDate: "data de nascimento",
  address: "endereco",
  name: "nome",
  description: "descricao",
  price: "preco",
  type: "tipo",
  recurringInterval: "recorrencia",
  role: "perfil",
  password: "senha",
  customerId: "cliente",
  productId: "produto",
  title: "titulo",
  message: "mensagem",
  contactLink: "link de contato",
  imageUrl: "imagem",
  daysOffset: "dias de antecedencia",
};

function getErrorPayload(error: unknown): Record<string, unknown> | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const candidate = error as {
    response?: { data?: Record<string, unknown> };
    data?: Record<string, unknown>;
  };

  if (candidate.response?.data && typeof candidate.response.data === "object") {
    return candidate.response.data;
  }

  if (candidate.data && typeof candidate.data === "object") {
    return candidate.data;
  }

  return candidate as Record<string, unknown>;
}

function formatDetailMessage(
  detail: ApiErrorDetail,
  fieldLabels: Record<string, string>,
) {
  const fieldLabel = detail.field ? fieldLabels[detail.field] ?? detail.field : null;
  const detailMessage = detail.message?.trim() || "Erro de validacao";

  if (detail.row && fieldLabel) {
    return `Linha ${detail.row} (${fieldLabel}): ${detailMessage}`;
  }

  if (detail.row) {
    return `Linha ${detail.row}: ${detailMessage}`;
  }

  if (fieldLabel) {
    return `${fieldLabel}: ${detailMessage}`;
  }

  return detailMessage;
}

function normalizeMessage(value: unknown): string | null {
  if (Array.isArray(value)) {
    const messages = value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean);
    return messages.length > 0 ? messages.join("\n") : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }

  return null;
}

export function extractApiErrorMessage(
  error: unknown,
  options: ExtractApiErrorOptions = {},
) {
  const {
    fallbackMessage = "Nao foi possivel concluir a operacao.",
    fieldLabels = defaultFieldLabels,
    maxDetails = 5,
  } = options;

  const payload = getErrorPayload(error);
  const details = Array.isArray(payload?.details)
    ? (payload.details as ApiErrorDetail[])
    : [];

  if (details.length > 0) {
    const visibleDetails = details
      .slice(0, maxDetails)
      .map((detail) => formatDetailMessage(detail, fieldLabels));

    if (details.length > maxDetails) {
      visibleDetails.push(`... e mais ${details.length - maxDetails} erro(s)`);
    }

    return visibleDetails.join("\n");
  }

  const payloadMessage = normalizeMessage(payload?.message);
  if (payloadMessage) {
    return payloadMessage;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }

  return fallbackMessage;
}

export function showApiErrorModal(
  modal: ModalLike,
  error: unknown,
  options: ShowApiErrorOptions,
) {
  const message = extractApiErrorMessage(error, options);

  modal?.show({
    type: "error",
    title: options.title,
    message,
    autoClose: options.autoClose ?? null,
  });

  return message;
}
